<script setup>
import Base64Link from 'module/base64Link/Base64Link.vue';
import SuggestionListItem from 'module/suggestionListItem/SuggestionListItem.vue';
import { onMounted, ref } from 'vue';
import { VisitedJobsHistory } from 'util/client-side-storage/visitedJobsHistory';
import { getJson } from 'util/xhr';
import { API_ENDPOINT_JOBS_BY_IDS } from 'app/endpoints';

const props = defineProps({
    jobId: { type: String, default: '' },
    lazy: { type: Boolean },
    recentJobs: { type: Boolean },
    headline: { type: String },
    hasMoreJobs: { type: Boolean },
    link: { type: String, default: '' },
    linkText: { type: String, default: '' },
    isLandingPage: { type: Boolean },
    hidden: { type: Boolean },
    items: { type: Array, default: () => [] },
});

const isHidden = ref(props.hidden);
const items = ref(props.items);

const numberOfItemsToLoad = 3;
async function loadRecentJobs() {
    const ids = (await VisitedJobsHistory.getJobIds())
        .filter((id) => id !== props.jobId)
        .splice(0, numberOfItemsToLoad);
    if (!ids || ids.length === 0) return;

    const queryString = ids.reduce((acc, id) => `${acc}jobIds[]=${id}&`, '?').slice(0, -1);
    getJson(API_ENDPOINT_JOBS_BY_IDS + queryString, {}, (response) => {
        if (!response || !Array.isArray(response)) return;
        items.value = response.map((item) => ({ jobsItem: { ...item.jobsItem, isRecentlyVisited: true } }));
        isHidden.value = false;
    });
}

onMounted(() => {
    if (props.lazy && props.recentJobs) {
        loadRecentJobs();
    }
});
</script>

<template>
    <div
        class="m-suggestionList"
        :class="{ 'm-suggestionList--hidden': isHidden }"
        :data-job-id="jobId"
        :data-lazy="lazy ? 'true' : undefined"
        :data-is-recent-jobs="recentJobs ? 'true' : undefined"
    >
        <div class="m-suggestionList__headlineWrapper">
            <h3 class="m-suggestionList__headline">
                {{ headline }}
            </h3>
            <div
                v-if="hasMoreJobs"
                class="m-suggestionList__moreJobsLinkWrapper"
            >
                <a
                    v-if="isLandingPage"
                    class="m-suggestionList__link m-suggestionList__link--landingpage"
                    :href="link"
                    data-event="click: similar-all"
                >
                    {{ linkText }}
                </a>
                <Base64Link
                    v-else
                    :link="link"
                    event="click: similar-all"
                    link-green
                >
                    {{ linkText }}
                </Base64Link>
            </div>
        </div>

        <ul class="m-suggestionList__list">
            <li
                v-for="(item, index) in items"
                :key="index"
                class="m-suggestionList__item"
            >
                <SuggestionListItem
                    v-bind="item.jobsItem"
                    :is-similar-job="item.isSimilarJob"
                    :pushed-job="item.pushedJob"
                    :is-recently-visited="item.isRecentlyVisited"
                />
            </li>
        </ul>

        <div
            v-if="hasMoreJobs"
            class="m-suggestionList__moreJobsLinkWrapperMobile"
        >
            <a
                v-if="isLandingPage"
                class="m-suggestionList__link m-suggestionList__link--mobileOnly m-suggestionList__link--landingpage"
                :href="link"
                data-event="click: similar-all"
            >
                {{ linkText }}
            </a>
            <Base64Link
                v-else
                :link="link"
                event="click: similar-all"
                link-green
            >
                <span class="m-suggestionList__link m-suggestionList__link--mobileOnly">{{ linkText }}</span>
            </Base64Link>
        </div>
    </div>
</template>

<style lang="scss">
@use "styles";
</style>
